/**
 * Supported UI languages. `pl` is primary; the rest fall back to `en` for any
 * missing keys (see src/i18n/index.ts `fallbackLng`).
 */
export const SUPPORTED_LANGUAGES = ['pl', 'en', 'ua', 'by'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Kod języka BCP-47 dla naszego kodu interfejsu. Nasze `ua` i `by` to kody KRAJÓW (i tak
 * zostają — adresy, zapisany wybór, nazwy plików); język ukraiński to `uk`, białoruski `be`
 * (ISO 639-1). Używają tego `<html lang>` (czytniki ekranu, dzielenie wyrazów, tłumacz
 * przeglądarki) i reguła liczby mnogiej w src/i18n/index.ts — jedno źródło mapowania.
 */
const KOD_BCP47: Record<SupportedLanguage, string> = { pl: 'pl', en: 'en', ua: 'uk', by: 'be' };

export function kodBcp47(lng: string | null | undefined): string {
  return isSupportedLanguage(lng) ? KOD_BCP47[lng] : 'pl';
}

/** Where an explicit user choice is persisted across reloads. */
export const LANGUAGE_STORAGE_KEY = 'fh_lang';

export function isSupportedLanguage(value: string | null | undefined): value is SupportedLanguage {
  return value != null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Map a BCP-47 browser locale (e.g. `en-US`, `uk`, `be-BY`) to one of our
 * supported app languages. Ukrainian's ISO code is `uk` (not `ua`) and
 * Belarusian's is `be` (not `by`), so both spellings are accepted. Returns
 * `null` when the locale isn't one we ship.
 */
export function mapBrowserLanguage(locale: string | null | undefined): SupportedLanguage | null {
  if (!locale) return null;
  const code = locale.toLowerCase();
  if (code.startsWith('pl')) return 'pl';
  if (code.startsWith('uk') || code.startsWith('ua')) return 'ua';
  if (code.startsWith('be') || code.startsWith('by')) return 'by';
  if (code.startsWith('en')) return 'en';
  return null;
}

/**
 * Initial language for the app. An explicit stored choice always wins; failing
 * that we default to the browser/system language (per the logged-out spec);
 * failing that, Polish.
 */
export function detectInitialLanguage(
  storedValue: string | null,
  browserLanguages: readonly string[],
): SupportedLanguage {
  if (isSupportedLanguage(storedValue)) return storedValue;
  for (const locale of browserLanguages) {
    const mapped = mapBrowserLanguage(locale);
    if (mapped) return mapped;
  }
  return 'pl';
}

/** Browser-environment wrapper around {@link detectInitialLanguage}. */
export function detectInitialLanguageFromEnv(): SupportedLanguage {
  let stored: string | null = null;
  try {
    stored = typeof localStorage !== 'undefined' ? localStorage.getItem(LANGUAGE_STORAGE_KEY) : null;
  } catch {
    stored = null;
  }
  const navLangs =
    typeof navigator !== 'undefined'
      ? (navigator.languages && navigator.languages.length > 0
          ? navigator.languages
          : [navigator.language])
      : [];
  return detectInitialLanguage(stored, navLangs);
}
